import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import pagesData from '../pages.json';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// The Weidner header builds its navigation dynamically from pages.json rather
// than a hardcoded list. Eligible pages are filtered, sorted by `position`,
// folded into a small set of "synthetic" umbrella dropdown groups, and any
// group that ends up with a single surviving child collapses to a plain link.
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** A page may name another page as its `parent`, nesting it as a sub-item. */
  parent?: string;
  sections?: { slot: string }[];
}

const PAGES = pagesData as Record<string, PageEntry>;

/** Page aliases that should never appear in the main navigation. */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'hyperlocal',
  'reviews',
]);

/**
 * Umbrella dropdown labels that don't correspond to a real page. Each groups
 * a set of real page aliases under one top-level nav entry. A group with a
 * single surviving child collapses to a plain link; an empty group is dropped.
 */
const SYNTHETIC_NAV_GROUPS: { label: string; aliases: string[] }[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'schedule-a-tour', 'virtual-tour'] },
];

const SYNTHETIC_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((g) => g.aliases));

interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 *  1. Filter eligible pages (excluded / hidden / template / data-gated out).
 *  2. Attach real `parent:`-declared children — skipping aliases owned by a
 *     synthetic group so they don't double-nest.
 *  3. Walk the top level in position order; when an alias belongs to a
 *     synthetic group, emit that group's dropdown instead (collapsing to a
 *     plain link when only one child survives, dropping it when none do).
 *  4. Force "Features" to sit before "Gallery & Tours".
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveMap: boolean): NavLink[] {
  const eligible = Object.entries(PAGES)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveMap) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();

  // Pass 1 — a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Pass 2 — attach real parent-declared children, but never for aliases owned
  // by a synthetic group (those are nested by the group instead).
  const realChildAliases = new Set<string>();
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_ALIASES.has(alias)) continue;
    if (
      page.parent &&
      page.parent !== alias &&
      eligibleAliases.has(page.parent) &&
      !SYNTHETIC_ALIASES.has(page.parent)
    ) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(linksByAlias.get(alias)!);
      realChildAliases.add(alias);
    }
  }

  // Pass 3 — build the top level in position order, emitting synthetic groups.
  const consumed = new Set<string>();
  const topLevel: NavLink[] = [];
  for (const [alias] of eligible) {
    if (consumed.has(alias) || realChildAliases.has(alias)) continue;

    const group = SYNTHETIC_NAV_GROUPS.find((g) => g.aliases.includes(alias));
    if (group) {
      const children = group.aliases
        .map((a) => linksByAlias.get(a))
        .filter((c): c is NavLink => Boolean(c));
      children.forEach((c) => consumed.add(c.alias!));
      if (children.length === 1) topLevel.push(children[0]);
      else if (children.length > 1) topLevel.push({ label: group.label, children });
      continue;
    }

    topLevel.push(linksByAlias.get(alias)!);
  }

  // Pass 4 — force "Features" ahead of "Gallery & Tours".
  const featuresIndex = topLevel.findIndex((l) => l.label === 'Features');
  const galleryIndex = topLevel.findIndex(
    (l) => l.label === 'Gallery & Tours' || l.alias === 'gallery',
  );
  if (featuresIndex > galleryIndex && galleryIndex >= 0) {
    const [features] = topLevel.splice(featuresIndex, 1);
    topLevel.splice(galleryIndex, 0, features);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: [
        'property_mono_white_logo',
        'property_primary_logo',
        'property_mono_black_logo',
        'property_secondary_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      suggestedFields: ['property_online_leasing_url', 'property_apply_now_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icons (inline, stroke="currentColor" so they inherit text color)
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

// ============================================================================
// Phone / URL formatting
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

// ============================================================================
// Desktop nav link — plain link or hover/focus dropdown parent
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!link.children || link.children.length === 0) {
    return (
      <a
        href={link.href}
        className="nav-link vantage-nav-darken-hover font-body text-[13px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap inline-flex items-center"
      >
        {link.label}
      </a>
    );
  }

  const Trigger = link.href ? 'a' : 'button';
  return (
    <div
      ref={wrapperRef}
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Trigger
        href={link.href}
        type={link.href ? undefined : 'button'}
        onClick={link.href ? undefined : () => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="nav-link font-body text-[13px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap inline-flex items-center gap-1.5"
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </Trigger>
      <div
        role="menu"
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)', borderColor: 'var(--vibe-color-border)' }}
      >
        {link.children.map((child) => (
          <a
            key={child.href}
            href={child.href}
            role="menuitem"
            className="block px-4 py-2.5 font-body text-[12px] font-semibold uppercase tracking-[0.1em] vantage-nav-darken-hover"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop nav — measured "More" overflow collapsing
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const available = container.clientWidth;
    const items = Array.from(measure.children) as HTMLElement[];
    const GAP = 28; // matches the desktop nav gap (gap-7 ≈ 1.75rem)
    const MORE_RESERVE = 90;

    // Pass A — do they all fit with no "More" button?
    let total = 0;
    for (let i = 0; i < items.length; i++) {
      total += (i > 0 ? GAP : 0) + items[i].getBoundingClientRect().width;
    }
    if (total <= available) {
      setVisibleCount(items.length);
      return;
    }

    // Pass B — reserve room for the "More" button and count what fits.
    let used = 0;
    let count = 0;
    for (let i = 0; i < items.length; i++) {
      const next = used + (i > 0 ? GAP : 0) + items[i].getBoundingClientRect().width;
      if (next + GAP + MORE_RESERVE <= available) {
        used = next;
        count++;
      } else {
        break;
      }
    }
    setVisibleCount(Math.max(0, count));
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recalculate());
    ro.observe(container);
    return () => ro.disconnect();
  }, [recalculate]);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <nav
      aria-label="Main navigation"
      className="hidden lg:flex flex-1 min-w-0 items-center justify-center"
    >
      {/* Hidden measurement row — renders every link (with chevrons) so their
          real widths can be measured off-screen. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none -z-10 flex items-center"
        style={{ left: -9999, top: -9999 }}
      >
        {navLinks.map((link) => (
          <span
            key={`measure-${link.label}`}
            className="font-body text-[13px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap inline-flex items-center gap-1.5"
          >
            {link.label}
            {link.children?.length ? <ChevronDownIcon className="w-3 h-3" /> : null}
          </span>
        ))}
      </div>

      <div ref={containerRef} className="flex items-center justify-center gap-7 w-full min-w-0">
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.alias || link.label} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative flex items-center"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              className="nav-link font-body text-[13px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap inline-flex items-center gap-1.5"
            >
              More
              <ChevronDownIcon
                className={`w-3 h-3 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              role="menu"
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border shadow-lg min-w-[220px] z-[70] transition-all duration-200 ${
                moreOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{
                backgroundColor: 'var(--vibe-color-background)',
                borderColor: 'var(--vibe-color-border)',
              }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.alias || link.label}>
                  <a
                    href={link.href || (link.children?.[0]?.href ?? '#')}
                    role="menuitem"
                    className="block px-4 py-2.5 font-body text-[12px] font-semibold uppercase tracking-[0.1em] vantage-nav-darken-hover"
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      role="menuitem"
                      className="block pl-9 pr-4 py-2 font-body text-[11px] font-medium uppercase tracking-[0.1em] vantage-nav-darken-hover"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// ============================================================================
// Mobile nav item — plain row or single-open accordion parent
// ============================================================================

function MobileNavItem({
  link,
  index,
  openIndex,
  setOpenIndex,
  onNavigate,
}: {
  link: NavLink;
  index: number;
  openIndex: number | null;
  setOpenIndex: (i: number | null) => void;
  onNavigate: () => void;
}) {
  const isOpen = openIndex === index;

  if (!link.children || link.children.length === 0) {
    return (
      <a
        href={link.href}
        onClick={onNavigate}
        className="mobile-nav-row block w-full py-3.5 font-body text-base font-semibold uppercase tracking-[0.1em]"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div className="mobile-nav-row">
      <button
        type="button"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between py-3.5 font-body text-base font-semibold uppercase tracking-[0.1em]"
      >
        {link.label}
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="pl-4 pb-2">
          {link.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              onClick={onNavigate}
              className="block py-2.5 font-body text-sm font-medium uppercase tracking-[0.08em] opacity-80"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection — solid once past 20px.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Measure header height so the mobile drawer can start exactly below the bar,
  // and publish it as a global CSS var so overlaid heroes can reserve the space
  // (keeps their centered title from sliding up behind the fixed header).
  useEffect(() => {
    const el = headerRef.current;
    const publish = (h: number) => {
      setHeaderHeight(h);
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--vibe-header-height', `${h}px`);
      }
    };
    if (!el || typeof ResizeObserver === 'undefined') {
      if (el) publish(el.offsetHeight);
      return;
    }
    const ro = new ResizeObserver(() => publish(el.offsetHeight));
    ro.observe(el);
    publish(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Virtual tours — gate the "Virtual Tour" nav item.
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — gated on the property's ApartmentSync (Repli360 / RRAC)
  // IDs, which drive the interactive leasing widget on that page. Both the
  // JavaScript ID and the Config ID must be present; a property with only one
  // is a half-configured state and the page is dropped from the nav (mirroring
  // the SSG hide + noindex logic). When the map drops out, "Find Your Home"
  // collapses to a plain "Floor Plans" link via the single-child rule.
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as string | null | undefined;
  const hasInteractiveMap =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveMap),
    [hasVirtualTours, hasInteractiveMap],
  );

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as
    | string
    | null
    | undefined;
  // Fall back to the property's primary phone when no call-tracking number is
  // set, so a property with only property_phone shows a real number instead of
  // the placeholder. (suggestedFields advertises property_phone but the runtime
  // chain otherwise never reads it.)
  const propertyPhone = usePropertyField('property_phone') as
    | string
    | null
    | undefined;
  const applyNowUrl = usePropertyField('property_apply_now_url') as
    | string
    | null
    | undefined;
  const onlineLeasingUrl = usePropertyField('property_online_leasing_url') as
    | string
    | null
    | undefined;
  const propertyWebsiteUrl = usePropertyField('property_website_url') as
    | string
    | null
    | undefined;

  // Apply Now points at the property's Apply Now URL — never the /apply-now page.
  // A slot value that still points at the internal apply-now page (a legacy
  // override) is treated as "unset" so it falls through to the real property
  // URL; a genuine custom (external) override is still respected. The explicit
  // field reads are graceful fallbacks for properties that haven't set an Apply
  // Now URL, so the button always lands on a real leasing destination.
  const applyNowPagePath = PAGES['apply-now']?.path || '/apply-now';
  const customApplyNowUrl =
    applyNowLink.url && applyNowLink.url !== applyNowPagePath ? applyNowLink.url : '';
  const applyNowHref =
    customApplyNowUrl || applyNowUrl || onlineLeasingUrl || propertyWebsiteUrl || '#';

  // Resident Access — defaults to the property's Resident Portal URL (or an
  // editor override). When neither is present the whole link is hidden rather
  // than pointing nowhere.
  const residentAccessHref = residentAccessLink.url || '';
  const hasResidentAccess = Boolean(residentAccessHref.trim());

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';

  const floorPlansHref = PAGES['floor-plans']?.path || '/floor-plans';

  // Schedule a Tour — an editor override URL wins; otherwise it lands on the
  // internal Schedule A Tour page.
  const scheduleTourPagePath = PAGES['schedule-a-tour']?.path || '/schedule-a-tour';
  const scheduleTourHref = scheduleTourLink.url || scheduleTourPagePath;
  const scheduleTourLabel = scheduleTourLink.label || 'Schedule a Tour';

  // Header background + logo state. Transparent at the top of every page (the
  // hero shows through, nav text is hardcoded white); solid white once scrolled.
  const headerSolid = scrolled || menuOpen;
  const logoImage = headerSolid
    ? monoBlackLogo || logoRaw.image
    : monoWhiteLogo || logoRaw.image;

  const backgroundColor = headerSolid ? 'var(--vibe-color-background)' : 'transparent';

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — the transparent header overlays the hero beneath it. */}
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out ${
          headerSolid ? 'header-scrolled' : 'header-transparent'
        }`}
        style={{ top: 0, backgroundColor }}
      >
        <div className="header-bar" style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}>
          {/* ============================================================ */}
          {/* Row A — utility row                                          */}
          {/* ============================================================ */}
          <div className="header-util-row h-9 flex items-center">
            {/* Desktop utility */}
            <div className="hidden lg:flex w-full items-center justify-between">
              <div className="flex items-center">
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                  <a href={phoneHref || '#'} className="header-util-link dniphone dniphonehref">
                    <PhoneIcon className="w-3.5 h-3.5" />
                    {phoneDisplay}
                  </a>
                </EditableSlotGroup>
                <span className="header-util-divider mx-2" aria-hidden="true" />
                <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                  <a
                    href={addressHref}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="header-util-link"
                  >
                    <MapPinIcon className="w-3.5 h-3.5" />
                    {addressLink.text || '123 Main Street, Atlanta, GA 30313'}
                  </a>
                </EditableSlotGroup>
              </div>
              <div className="flex items-center">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowHref}
                    target={applyNowLink._target || '_self'}
                    className="header-util-link"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
                {hasResidentAccess && (
                  <>
                    <span className="header-util-divider mx-2" aria-hidden="true" />
                    <EditableSlotGroup
                      groupPath="header.resident_access_link"
                      definition={residentAccessLinkDef}
                    >
                      <a
                        href={residentAccessHref}
                        target={residentAccessLink._target || '_self'}
                        className="header-util-link"
                      >
                        {residentAccessLink.label || 'Resident Access'}
                      </a>
                    </EditableSlotGroup>
                  </>
                )}
              </div>
            </div>

            {/* Mobile utility — phone + directions */}
            <div className="flex lg:hidden w-full items-center justify-between">
              <a href={phoneHref || '#'} className="header-util-link dniphone dniphonehref">
                <PhoneIcon className="w-4 h-4" />
                {phoneDisplay}
              </a>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="header-util-link"
              >
                <MapPinIcon className="w-4 h-4" />
                Get Directions
              </a>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Row B — main nav row                                         */}
          {/* ============================================================ */}
          <div className="header-main-row flex items-center gap-3 lg:gap-4 h-16 lg:h-[72px]">
            {/* Logo — allowed to shrink on mobile (min-w-0 + shrink) so the gap
                to the CTA is always honored instead of the two colliding. On
                desktop the nav handles spacing, so the logo keeps full size. */}
            <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
              <a href="/" className="min-w-0 shrink lg:shrink-0">
                {logoImage ? (
                  <VibesitesImage
                    url={logoImage}
                    alt={logoRaw.alt || 'Community Logo'}
                    className="h-10 lg:h-12 w-auto max-w-full object-contain"
                  />
                ) : (
                  <div className="font-heading text-xl lg:text-2xl font-bold">The Rise</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Desktop navigation */}
            <DesktopNav navLinks={navLinks} />

            {/* Right group — Schedule a Tour CTA + hamburger */}
            <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
              {/* Schedule a Tour — primary CTA button (desktop only) */}
              <EditableSlotGroup
                className="hidden lg:block"
                groupPath="header.schedule_tour_link"
                definition={scheduleTourLinkDef}
              >
                <a
                  href={scheduleTourHref}
                  target={scheduleTourLink._target || '_self'}
                  className="header-cta group relative inline-flex items-center justify-center gap-2 px-5 py-2.5 font-body text-[12px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap overflow-hidden"
                >
                  <span className="relative z-10">{scheduleTourLabel}</span>
                  <ArrowRightIcon className="relative z-10 w-3.5 h-3.5" />
                </a>
              </EditableSlotGroup>

              {/* Hamburger — mobile only */}
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile drawer — solid white panel below the measured header    */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          {/* Extra bottom padding (pb-40) reserves clearance for the
              Rentgrata "Ask a Real Resident" floating widget, which is
              injected at runtime and pins itself to the bottom of the
              viewport. Without this the widget's fixed click surface
              overlaps the Apply Now / Resident Access buttons and the
              address/phone contact block above, making them un-tappable
              on mobile. The drawer panel is `overflow-y-auto`, so this
              padding simply adds scrollable room letting every control
              clear the widget. */}
          <div className="px-6 pt-6 pb-40">
            <nav aria-label="Mobile navigation" className="mobile-nav border-b" style={{ borderColor: 'var(--vibe-color-border)' }}>
              {navLinks.map((link, index) => (
                <MobileNavItem
                  key={link.alias || link.label}
                  link={link}
                  index={index}
                  openIndex={openMobileAccordion}
                  setOpenIndex={setOpenMobileAccordion}
                  onNavigate={closeMobileMenu}
                />
              ))}
            </nav>

            {/* Primary CTA — Schedule a Tour */}
            <EditableSlotGroup
              className="block mt-6"
              groupPath="header.schedule_tour_link"
              definition={scheduleTourLinkDef}
            >
              <a
                href={scheduleTourHref}
                target={scheduleTourLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-primary-cta group relative flex items-center justify-center gap-2 w-full py-3.5 font-body text-sm font-semibold uppercase tracking-[0.12em] overflow-hidden"
              >
                <span className="relative z-10">{scheduleTourLabel}</span>
                <ArrowRightIcon className="relative z-10 w-4 h-4" />
              </a>
            </EditableSlotGroup>

            {/* Secondary CTA — View Floor Plans (matches the Schedule a Tour
                button's filled treatment for a consistent look) */}
            <a
              href={floorPlansHref}
              onClick={closeMobileMenu}
              className="mobile-primary-cta group relative mt-3 flex items-center justify-center gap-2 w-full py-3.5 font-body text-sm font-semibold uppercase tracking-[0.12em] overflow-hidden"
            >
              <span className="relative z-10">View Floor Plans</span>
              <ArrowRightIcon className="relative z-10 w-4 h-4" />
            </a>

            {/* Secondary CTA pills */}
            <div className="mt-4 flex items-stretch gap-3">
              <EditableSlotGroup
                className="flex-1"
                groupPath="header.apply_now_link"
                definition={applyNowLinkDef}
              >
                <a
                  href={applyNowHref}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="mobile-secondary-cta block w-full text-center py-3 font-body text-xs font-semibold uppercase tracking-[0.1em]"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              {hasResidentAccess && (
                <EditableSlotGroup
                  className="flex-1"
                  groupPath="header.resident_access_link"
                  definition={residentAccessLinkDef}
                >
                  <a
                    href={residentAccessHref}
                    target={residentAccessLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="mobile-secondary-cta block w-full text-center py-3 font-body text-xs font-semibold uppercase tracking-[0.1em]"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              )}
            </div>

            {/* Contact block */}
            <div className="mt-6 space-y-4">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="mobile-contact-row group flex items-start gap-3"
                >
                  <MapPinIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="vantage-nav-darken-hover font-body text-sm font-medium">
                    {addressLink.text || '123 Main Street, Atlanta, GA 30313'}
                  </span>
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                <a
                  href={phoneHref || '#'}
                  className="mobile-contact-row dniphone dniphonehref group flex items-center gap-3"
                >
                  <PhoneIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="vantage-nav-darken-hover font-body text-sm font-medium">
                    {phoneDisplay}
                  </span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Layout                                                          */
        /* ================================================================ */
        .header-bar {
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          border-bottom: 1px solid transparent;
          transition: border-color 0.4s ease, box-shadow 0.4s ease;
        }
        @media (min-width: 1024px) {
          .header-bar {
            padding-left: 2.5rem;
            padding-right: 2.5rem;
          }
        }

        /* Scrolled (solid white) — fade in border + shadow. */
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* ================================================================ */
        /* Nav links                                                       */
        /* ================================================================ */
        .nav-link {
          color: var(--vibe-color-text);
          text-decoration: none;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s ease;
        }
        /* Animated underline affordance (rides the text baseline). */
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 5px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover,
        a:hover > .vantage-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }

        /* Transparent header (top of any page) → nav text hardcoded white. */
        .header-transparent .header-bar .nav-link,
        .header-transparent .header-bar .nav-link:hover {
          color: #ffffff;
        }

        /* Dropdown panels sit on a white surface even when the bar above is
           transparent — force theme text so it never goes white-on-white. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* ================================================================ */
        /* Schedule a Tour CTA (desktop) — filled primary button with a       */
        /* shimmer sweep on hover. Stays solid in both header states so it     */
        /* reads as a button over the transparent hero and the white bar.     */
        /* ================================================================ */
        .header-cta {
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(135deg, var(--vibe-color-primary),
            color-mix(in srgb, var(--vibe-color-primary), black 18%));
          border-radius: 2px;
          text-decoration: none;
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }
        .header-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent,
            color-mix(in srgb, #fff 35%, transparent), transparent);
          transition: transform 0.6s ease;
        }
        .header-cta:hover::before {
          transform: translateX(100%);
        }
        .header-cta:hover {
          box-shadow: 0 4px 14px color-mix(in srgb, var(--vibe-color-primary) 45%, transparent);
        }
        /* Keep the CTA label on-primary even when the transparent-header rule
           tries to force nav text white. */
        .header-transparent .header-bar .header-cta,
        .header-transparent .header-bar .header-cta:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* ================================================================ */
        /* Utility links                                                   */
        /* ================================================================ */
        /* Utility strip — a full-bleed property-primary band, independent of the
           header's transparent/scrolled state. It breaks out of the header's
           horizontal padding so the color spans edge to edge while content stays
           aligned with the bar below. Text is the on-primary token. */
        .header-util-row {
          background: var(--vibe-color-primary);
          margin-left: -1.5rem;
          margin-right: -1.5rem;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }
        @media (min-width: 1024px) {
          .header-util-row {
            margin-left: -2.5rem;
            margin-right: -2.5rem;
            padding-left: 2.5rem;
            padding-right: 2.5rem;
          }
        }
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 88%, transparent);
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 5px;
          white-space: nowrap;
          transition: color 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        .header-util-link:hover {
          color: var(--vibe-color-text-on-primary);
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 35%, transparent);
          flex-shrink: 0;
        }

        /* Transparent header → hamburger bars + logo fallback go white. */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* ================================================================ */
        /* Mobile drawer (solid white surface → theme text)               */
        /* ================================================================ */
        .mobile-menu-panel .mobile-nav-row {
          color: var(--vibe-color-text);
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .mobile-menu-panel .mobile-nav-row a,
        .mobile-menu-panel .mobile-nav-row button {
          color: var(--vibe-color-text);
        }
        .mobile-menu-panel .mobile-contact-row {
          color: var(--vibe-color-text-muted);
          transition: color 0.2s ease;
        }
        .mobile-menu-panel .mobile-contact-row:hover {
          color: var(--vibe-color-primary);
        }

        /* Primary CTA — filled gradient with a shimmer sweep on hover. */
        .mobile-primary-cta {
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(135deg, var(--vibe-color-primary),
            color-mix(in srgb, var(--vibe-color-primary), black 18%));
          border-radius: 2px;
        }
        .mobile-primary-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent,
            color-mix(in srgb, #fff 35%, transparent), transparent);
          transition: transform 0.6s ease;
        }
        .mobile-primary-cta:hover::before {
          transform: translateX(100%);
        }

        /* Secondary pills — black label on a primary-colored outline that fills
           with primary on hover. */
        .mobile-secondary-cta {
          color: #000000;
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          transition: color 0.2s ease, background-color 0.2s ease;
        }
        .mobile-secondary-cta:hover {
          color: var(--vibe-color-text-on-primary);
          background-color: var(--vibe-color-primary);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          defaultField: 'property_mono_white_logo',
          suggestedFields: [
            'property_mono_white_logo',
            'property_primary_logo',
            'property_mono_black_logo',
            'property_secondary_logo',
          ],
        },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          suggestedFields: ['property_online_leasing_url', 'property_apply_now_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        text: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.phone_link': {
      label: 'Phone Link',
      fields: {
        text: {
          label: 'Phone Text',
          type: 'text',
          defaultField: 'property_call_tracking_phone',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Header;
